import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { instructorStudentsAPI, supervisionAPI } from '../../api';
import useSupervisionWebSocket from '../../hooks/useSupervisionWebSocket';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Eye, 
  EyeOff,
  Monitor,
  MessageSquare,
  Send,
  Maximize2,
  Minimize2,
  Play,
  Pause,
  Hand,
  Lightbulb,
  AlertTriangle,
  RefreshCw,
  Users,
  Activity,
  Heart,
  Thermometer,
  Wind,
  Droplets,
  Grid3X3,
  LayoutGrid,
  X,
  Wifi,
  WifiOff
} from 'lucide-react';

// Componente de tarjeta de simulación individual
const SimulationCard = ({ 
  session, 
  isExpanded, 
  onExpand, 
  onSupervise,
  isSupervising,
  wsData 
}) => {
  const student = session.student || {};
  const scenario = session.scenario || {};
  
  return (
    <Card className={`transition-all duration-300 ${isExpanded ? 'col-span-2 row-span-2' : ''} ${isSupervising ? 'ring-2 ring-blue-500' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-sm font-medium">
              {student.nombre} {student.apellido}
            </CardTitle>
            <p className="text-xs text-gray-500">{scenario.nombre || 'Escenario'}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={session.status === 'en_curso' ? 'default' : 'secondary'} className="text-xs">
              {session.status}
            </Badge>
            {isSupervising && (
              <Badge className="bg-green-500 text-xs animate-pulse">
                <Wifi className="w-3 h-3 mr-1" />
                Conectado
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Signos Vitales */}
        {wsData?.vitals ? (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1 bg-red-50 p-2 rounded">
              <Heart className="w-3 h-3 text-red-500" />
              <span className="font-mono">{wsData.vitals.fc || '--'} bpm</span>
            </div>
            <div className="flex items-center gap-1 bg-blue-50 p-2 rounded">
              <Activity className="w-3 h-3 text-blue-500" />
              <span className="font-mono">{wsData.vitals.pas || '--'}/{wsData.vitals.pad || '--'}</span>
            </div>
            <div className="flex items-center gap-1 bg-cyan-50 p-2 rounded">
              <Droplets className="w-3 h-3 text-cyan-500" />
              <span className="font-mono">{wsData.vitals.spo2 || '--'}%</span>
            </div>
            <div className="flex items-center gap-1 bg-orange-50 p-2 rounded">
              <Thermometer className="w-3 h-3 text-orange-500" />
              <span className="font-mono">{wsData.vitals.temp || '--'}°C</span>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-400 text-sm">
            <Monitor className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Conecta para ver signos vitales</p>
          </div>
        )}
        
        {/* Acciones */}
        <div className="flex gap-2 pt-2 border-t">
          <Button
            size="sm"
            variant={isSupervising ? "default" : "outline"}
            className="flex-1 text-xs"
            onClick={() => onSupervise(session.id)}
          >
            {isSupervising ? (
              <>
                <EyeOff className="w-3 h-3 mr-1" />
                Desconectar
              </>
            ) : (
              <>
                <Eye className="w-3 h-3 mr-1" />
                Supervisar
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onExpand(session.id)}
          >
            {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Panel de Chat e Intervención
const InterventionPanel = ({
  messages,
  onSendMessage,
  onSendHint,
  supervisionMode,
  onModeChange,
  hasControl,
  onTakeControl,
  onReleaseControl,
  sessionInfo
}) => {
  const [chatInput, setChatInput] = useState('');
  const [hintInput, setHintInput] = useState('');
  const [showHintInput, setShowHintInput] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendChat = (e) => {
    e.preventDefault();
    if (chatInput.trim()) {
      onSendMessage(chatInput);
      setChatInput('');
    }
  };

  const handleSendHint = (type = 'sugerencia') => {
    if (hintInput.trim()) {
      onSendHint(hintInput, type);
      setHintInput('');
      setShowHintInput(false);
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle className="text-sm">Panel de Intervención</CardTitle>
          <div className="flex items-center gap-2">
            {sessionInfo?.total_connections > 0 && (
              <Badge variant="outline" className="text-xs">
                <Users className="w-3 h-3 mr-1" />
                {sessionInfo.total_connections}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col space-y-4 overflow-hidden">
        {/* Controles de Modo */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-500">Modo de Supervisión</label>
          <div className="flex gap-2">
            <Select value={supervisionMode} onValueChange={onModeChange}>
              <SelectTrigger className="flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="observador">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Observador
                  </div>
                </SelectItem>
                <SelectItem value="asistido">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    Asistido
                  </div>
                </SelectItem>
                <SelectItem value="control_total">
                  <div className="flex items-center gap-2">
                    <Hand className="w-4 h-4" />
                    Control Total
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {supervisionMode === 'control_total' && (
            <div className="flex gap-2">
              {!hasControl ? (
                <Button size="sm" className="flex-1" onClick={onTakeControl}>
                  <Hand className="w-4 h-4 mr-1" />
                  Tomar Control
                </Button>
              ) : (
                <Button size="sm" variant="destructive" className="flex-1" onClick={onReleaseControl}>
                  <X className="w-4 h-4 mr-1" />
                  Liberar Control
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Botones de Intervención Rápida */}
        {(supervisionMode === 'asistido' || supervisionMode === 'control_total') && (
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-500">Intervención Rápida</label>
            {showHintInput ? (
              <div className="space-y-2">
                <Input
                  value={hintInput}
                  onChange={(e) => setHintInput(e.target.value)}
                  placeholder="Escribe tu sugerencia..."
                  className="text-sm"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleSendHint('sugerencia')} className="flex-1">
                    <Lightbulb className="w-3 h-3 mr-1" />
                    Sugerencia
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleSendHint('alerta')}>
                    <AlertTriangle className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowHintInput(false)}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ) : (
              <Button 
                size="sm" 
                variant="outline" 
                className="w-full"
                onClick={() => setShowHintInput(true)}
              >
                <Lightbulb className="w-4 h-4 mr-2" />
                Enviar Pista al Estudiante
              </Button>
            )}
          </div>
        )}

        {/* Chat */}
        <div className="flex-1 flex flex-col min-h-0">
          <label className="text-xs font-medium text-gray-500 mb-2">Chat</label>
          <div className="flex-1 overflow-y-auto bg-gray-50 rounded-lg p-2 space-y-2 min-h-[150px]">
            {messages.length === 0 ? (
              <p className="text-center text-gray-400 text-xs py-4">
                Sin mensajes aún
              </p>
            ) : (
              messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`p-2 rounded-lg text-xs ${
                    msg.isHint 
                      ? 'bg-yellow-100 border-l-4 border-yellow-500' 
                      : msg.sender_role === 'instructor'
                        ? 'bg-blue-100 ml-4'
                        : 'bg-white mr-4'
                  }`}
                >
                  <div className="font-medium text-gray-700">
                    {msg.sender_name || 'Usuario'}
                    {msg.isHint && <Badge className="ml-2 text-xs bg-yellow-500">Pista</Badge>}
                  </div>
                  <div className="text-gray-600">{msg.content}</div>
                  <div className="text-gray-400 text-xs mt-1">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
          
          <form onSubmit={handleSendChat} className="flex gap-2 mt-2">
            <Input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Escribe un mensaje..."
              className="flex-1 text-sm"
            />
            <Button type="submit" size="sm">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  );
};

// Página principal de Supervisión en Vivo
export default function LiveSupervisionPage() {
  const { user } = useAuth();
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gridSize, setGridSize] = useState('2x2');
  const [expandedSession, setExpandedSession] = useState(null);
  const [supervisingSessionId, setSupervisingSessionId] = useState(null);
  const [courseFilter, setCourseFilter] = useState('all');

  // Get token from a ref to avoid stale closure issues
  const getToken = useCallback(() => localStorage.getItem('token'), []);
  
  // WebSocket para la sesión activa supervisada
  const {
    isConnected,
    messages,
    vitals,
    supervisionMode,
    hasControl,
    sessionInfo,
    sendChatMessage,
    sendHint,
    takeControl,
    releaseControl,
    setMode,
    disconnect
  } = useSupervisionWebSocket(supervisingSessionId, getToken(), 'instructor');

  // Cargar sesiones activas
  const loadActiveSessions = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (courseFilter && courseFilter !== 'all') {
        params.course_id = courseFilter;
      }
      const response = await instructorStudentsAPI.getActiveSessions(params);
      setActiveSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error cargando sesiones:', error);
      toast.error('Error al cargar sesiones activas');
    } finally {
      setLoading(false);
    }
  }, [courseFilter]);

  useEffect(() => {
    loadActiveSessions();
    // Refrescar cada 30 segundos
    const interval = setInterval(loadActiveSessions, 30000);
    return () => clearInterval(interval);
  }, [loadActiveSessions]);

  // Manejar supervisión
  const handleSupervise = async (sessionId) => {
    if (supervisingSessionId === sessionId) {
      // Desconectar
      disconnect();
      setSupervisingSessionId(null);
      toast.info('Supervisión finalizada');
    } else {
      // Conectar a nueva sesión
      if (supervisingSessionId) {
        disconnect();
      }
      setSupervisingSessionId(sessionId);
      toast.success('Conectando a la simulación...');
      
      // Registrar inicio de supervisión en BD
      try {
        await supervisionAPI.startSupervision(sessionId, 'observador');
      } catch (error) {
        console.error('Error registrando supervisión:', error);
      }
    }
  };

  // Grid sizes
  const gridClasses = {
    '2x2': 'grid-cols-2 grid-rows-2',
    '2x3': 'grid-cols-3 grid-rows-2',
    '3x3': 'grid-cols-3 grid-rows-3',
    'auto': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col p-4 bg-gray-100">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Monitor className="w-6 h-6 text-blue-600" />
            Centro de Supervisión en Vivo
          </h1>
          <p className="text-sm text-gray-600">
            {activeSessions.length} simulaciones activas
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Indicador de conexión */}
          {supervisingSessionId && (
            <Badge variant={isConnected ? "default" : "secondary"} className="flex items-center gap-1">
              {isConnected ? (
                <>
                  <Wifi className="w-3 h-3" />
                  Conectado
                </>
              ) : (
                <>
                  <WifiOff className="w-3 h-3" />
                  Desconectado
                </>
              )}
            </Badge>
          )}
          
          {/* Selector de grid */}
          <Select value={gridSize} onValueChange={setGridSize}>
            <SelectTrigger className="w-[120px]">
              <Grid3X3 className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2x2">2x2 (4)</SelectItem>
              <SelectItem value="2x3">2x3 (6)</SelectItem>
              <SelectItem value="3x3">3x3 (9)</SelectItem>
              <SelectItem value="auto">Auto</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Refrescar */}
          <Button variant="outline" size="sm" onClick={loadActiveSessions}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Grid de simulaciones */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : activeSessions.length === 0 ? (
            <Card className="h-full flex items-center justify-center">
              <CardContent className="text-center py-12">
                <Monitor className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">
                  No hay simulaciones activas
                </h3>
                <p className="text-gray-500 mt-2">
                  Las simulaciones de tus estudiantes aparecerán aquí
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={loadActiveSessions}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualizar
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className={`grid gap-4 h-full ${gridClasses[gridSize]}`}>
              {activeSessions.map((session) => (
                <SimulationCard
                  key={session.id}
                  session={session}
                  isExpanded={expandedSession === session.id}
                  onExpand={(id) => setExpandedSession(expandedSession === id ? null : id)}
                  onSupervise={handleSupervise}
                  isSupervising={supervisingSessionId === session.id && isConnected}
                  wsData={supervisingSessionId === session.id ? { vitals, messages } : null}
                />
              ))}
            </div>
          )}
        </div>

        {/* Panel lateral de intervención */}
        {supervisingSessionId && (
          <div className="w-80 flex-shrink-0">
            <InterventionPanel
              messages={messages}
              onSendMessage={sendChatMessage}
              onSendHint={sendHint}
              supervisionMode={supervisionMode}
              onModeChange={setMode}
              hasControl={hasControl}
              onTakeControl={takeControl}
              onReleaseControl={releaseControl}
              sessionInfo={sessionInfo}
            />
          </div>
        )}
      </div>
    </div>
  );
}
